'use client'

import { useState, useRef, useEffect } from "react"
import { ChevronDown } from "lucide-react"

export type FilterOption = { v: string; l: string }

export function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (v: string) => void
  options: FilterOption[]
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const selected = options.find((o) => o.v === value) ?? options[0]

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-brand-card border border-brand-stroke text-brand-text text-sm global-border-radius px-3 py-2.5 pr-8 hover:border-brand-green/40 focus:outline-none focus:border-brand-green/50 transition-colors duration-200 whitespace-nowrap 3xl:text-xl"
      >
        {selected.l}
        <ChevronDown
          className={`absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-30 mt-1.5 min-w-full global-border-radius border border-brand-stroke bg-brand-card shadow-lg overflow-hidden">
          {options.map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => { onChange(o.v); setOpen(false) }}
              className={[
                "w-full text-left px-3 py-2 text-sm transition-colors duration-100 3xl:text-xl",
                o.v === value
                  ? "bg-brand-surface text-brand-text font-semibold"
                  : "text-brand-muted hover:bg-brand-surface/60 hover:text-brand-text",
              ].join(" ")}
            >
              {o.l}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
